import { useMutation } from '@tanstack/react-query';

import { campaignsEndpoints as endpoints } from '@/api/endpoints';
import { callPost } from '@/api/fetcher';

import {
  SuggestColumnMappingsRequest,
  SuggestColumnMappingsResponse,
} from '../types/column-mapping';

/**
 * API function for suggesting column mappings
 */
const suggestColumnMappings = async (
  data: SuggestColumnMappingsRequest
): Promise<SuggestColumnMappingsResponse> => callPost(endpoints.suggestColumnMappings, data);

/**
 * Hook for suggesting column mappings using AI
 *
 * Uses TanStack Query mutation to call the backend API that suggests
 * mappings from CSV columns to system variables.
 *
 * @example
 * ```tsx
 * const { suggestMappings, isPending, data } = useColumnMappingSuggestions();
 *
 * const handleAnalyze = async () => {
 *   const result = await suggestMappings({
 *     headers: ['Name', 'Phone', 'Amount'],
 *     sample_rows: [['John', '1234567890', '500']],
 *     available_variables: DEBT_CAMPAIGN_VARIABLES,
 *   });
 * };
 * ```
 */
export const useColumnMappingSuggestions = () => {
  const {
    mutate: suggestMappings,
    mutateAsync: suggestMappingsAsync,
    data,
    isPending,
    isError,
    error,
    reset,
  } = useMutation<SuggestColumnMappingsResponse, Error, SuggestColumnMappingsRequest>({
    mutationFn: suggestColumnMappings,
  });

  return {
    suggestMappings,
    suggestMappingsAsync,
    suggestions: data?.mappings,
    isPending,
    isError,
    error,
    reset,
  };
};
